import React, { forwardRef } from "react";
import { TextInputBase } from "../../text-input/text-input-base.jsx";
import Popover from "../../popover";
import { CvvProps } from "../types/cvv.types";
import classnames from "classnames";
import {
  showSSRHydrationWarning,
  isPropEmpty,
} from "../../utils/SSR/showSSRHydrationWarning";
import { useSSR } from "../../utils/SSR/useSSR";

export interface CvvForwardRef
  extends React.ForwardRefExoticComponent<
    CvvProps & React.RefAttributes<HTMLInputElement>
  > {}

export const CvvField = forwardRef<HTMLInputElement, CvvProps>(
  (
    {
      placeHolder = "CVV",
      name,
      label = "CVV",
      cvvLength = 3,
      onClick,
      className,
      showPopover = true,
      cvvHeader = "CVV Security Code",
      cvvBody = "Find the 3-digit security code on the back of your credit card, by the signature line, or on the front of your Amex card, by the card number.",
      cvvImage = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAHgAAAB4CAMAAAAOusbgAAAANlBMVEXMzMwZTGYjU2xTdYeyub0kVGyxuLwhUmsAAAD///+goKAzMzNBQUHW1ta7u7tOTk6FhYXx8fEuevcHAAAAoUlEQVR4Ae3XNYLDMBBAUTOIdf/D7oa6KRPx/6HpXsz2RERERERE3zcnbFm3IvCj/SgEz2sp+CwFz8DAwOnhK3EdwcDAwMDAwMDAwMA8STQMAwMDA98/rEUYGFhprZUcksPK2NsZJYfUsA+Pr+DlkBiOWr1WbpRDWth+GCsH4E62cbG9+lba3U4rOfR45uLqBAzMk0R6GBgYGBgYGBgYGPgf8O2eSCD/1hgAAAAASUVORK5CYII=",
      cvvImageAlt = "cvv on card",
      id,
      ...rest
    },
    ref
  ) => {
    const classes = classnames("spark-payment--card-security-code", className);
    useSSR(
      () =>
        showPopover &&
        isPropEmpty(id) &&
        showSSRHydrationWarning("id", "CvvField")
    );
    return (
      <TextInputBase
        className={classes}
        placeHolder={placeHolder}
        name={name}
        label={label}
        typeaheadFormat={cvvLength === 3 ? "\\d\\d\\d" : "\\d\\d\\d\\d"}
        isTypeahead={true}
        isCvv={true}
        ref={ref}
        maxLength={cvvLength}
        minLength={3}
        {...rest}
        type={"tel"}
      >
        {showPopover && (
          <div
            style={{
              position: "absolute",
              zIndex: 10,
              top: "1rem",
              right: "1rem",
            }}
          >
            <Popover
              id={id ? `${id}-popover` : undefined}
              anchorX="center"
              anchorY="bottom"
              toggleEl={
                <button
                  name="msg"
                  type="button"
                  className="spark-btn--icon spark-icon spark-icon-question-mark-circle"
                  style={{ width: "3rem", height: "3rem", padding: 0 }}
                />
              }
              closeButton
            >
              <h3
                style={{
                  fontSize: "1.66666667rem",
                  textTransform: "uppercase",
                }}
              >
                {cvvHeader}
              </h3>
              <p
                style={{ maxWidth: 267, whiteSpace: "normal" }}
                className="spark-mar-b-0"
              >
                <img
                  alt={cvvImageAlt}
                  className="example"
                  style={{
                    borderRadius: "50%",
                    display: "inline-block",
                    float: "right",
                    height: "120px",
                    margin: "0 0 0 0.5rem",
                    width: "120px",
                  }}
                  src={cvvImage}
                />
                {cvvBody}
              </p>
            </Popover>
          </div>
        )}
      </TextInputBase>
    );
  }
) as CvvForwardRef;

CvvField.displayName = "CvvField";
